(function () {
    'use strict';

    // FOS-20691: ui-router states inside the Jobs/AiRForms experience that must bounce to
    // their React (sora) equivalent — via URL, bookmark or any internal $state.go — when the
    // modernized Jobs/AiRForms flag is enabled for the tenant. Settings sub-states (job
    // templates, exports, schedulers, etc.) are intentionally excluded; they aren't part of
    // this flag's scope.
    var MODERNIZED_JOBS_AIRFORMS_STATES = [
        'storm.jobs',
        'storm.jobs-search',
        'storm.job-execute',
        'storm.job-schedule',
        'storm.job-detail',
        'storm.job-detail-by-job-number',
        'storm.new-job',
        'storm.edit-job',
        'storm.edit-job-by-job-number',
        'storm.admin-airforms',
        'storm.admin-airform-detail',
        'storm.admin-airform-dashboard',
        'storm.admin-airforms-preview'
    ];
    // FOS-21392: the AiR Insights, AiR Sessions, AiR Tickets and AiR Content states bounce
    // to sora on their own modernized UI flag, separate from Jobs/AiRForms. The content
    // viewer (storm.content-view) stays here; sora has no equivalent of it.
    var MODERNIZED_UI_STATES = [
        'storm.insights.adv-dashboards',
        'storm.insights.adv-analyze',
        'storm.insights.dashboard-detail',
        'storm.insights.dashboard-view',
        'storm.insights.workspace-summary',
        'storm.insights.conversations',
        'storm.insights.contents',
        'storm.insights.topics',
        'storm.insights.sessions',
        'storm.insights.airforms',
        'storm.insights.users',
        'storm.sessions.upcoming',
        'storm.sessions.upcoming-detail',
        'storm.sessions.past',
        'storm.sessions.past-recording',
        'storm.sessions.personal',
        'storm.sessions.all',
        'storm.sessions.templates',
        'storm.sessions.template-detail',
        'storm.sessions.player',
        'storm.tickets',
        'storm.admin-contents',
        'storm.content-documents',
        'storm.admin-content-detail',
        'storm.admin-content-detail-recognition.section',
        'storm.admin-content-detail-insights.section',
        'storm.admin-content-fields'
    ];
    // Built-in insights reports; sora resolves each dashboard id to its own report screen.
    var LEGACY_INSIGHTS_REPORT_STATES = [
        'storm.insights.workspace-summary',
        'storm.insights.conversations',
        'storm.insights.contents',
        'storm.insights.topics',
        'storm.insights.sessions',
        'storm.insights.airforms',
        'storm.insights.users'
    ];
    // Content details states and the sora details tab each opens on (none = Settings).
    var CONTENT_DETAIL_TABS = {
        'storm.admin-content-detail': null,
        'storm.admin-content-detail-recognition.section': 'recognition',
        'storm.admin-content-detail-insights.section': 'insights'
    };
    var MODERNIZED_JOBS_AIRFORMS_FEATURE = 'feature_jobs_airforms_modernized_jobs_airforms_experience';
    var MODERNIZED_UI_FEATURE = 'feature_jobs_airforms_modernized_ui_experience';

    angular
        .module('atheer')
        .factory('stateHandler', stateHandler);

    /* @ngInject */
    function stateHandler($rootScope, $state, $cookies, $translate, JhiLanguageService, translationHandler, Auth, Feature, Principal, VERSION, postMessageSecurity) {
        return {
            initialize: initialize
        };

        // sora is served under the /new base path. The states below are named differently
        // in sora (e.g. fos-web's '/airforms-preview/:id' is sora's '/airforms/:id/preview');
        // every other state shares its path 1:1 with sora, so only gets the /new prefix.
        // Returns null for a state that should stay in fos-web.
        function resolveModernizedExperiencePath(toState, toStateParams) {
            var id = toStateParams.id ? encodeURIComponent(toStateParams.id) : '';

            if (toState.name === 'storm.admin-airforms-preview') {
                return '/new/airforms/' + id + '/preview';
            }

            if (toState.name === 'storm.insights.dashboard-detail') {
                return id ? '/new/insights/dashboards/' + id : '/new/insights/dashboards';
            }
            if (toState.name === 'storm.insights.dashboard-view') {
                if (!id) {
                    return '/new/insights/dashboards';
                }
                return '/new/insights/dashboards/' + id + (toStateParams.dashboardmode === 'edit' ? '/builder' : '/open');
            }
            if (LEGACY_INSIGHTS_REPORT_STATES.indexOf(toState.name) !== -1) {
                return id ? '/new/insights/dashboards/' + id + '/open' : '/new/insights/dashboards';
            }

            // The schedule form: '/upcoming/' is a new session (optionally for an asset),
            // '/upcoming/:id' edits one.
            if (toState.name === 'storm.sessions.upcoming-detail') {
                if (id) {
                    return '/new/sessions/' + id + '/edit';
                }
                return toStateParams.asset
                    ? '/new/sessions/new?asset=' + encodeURIComponent(toStateParams.asset)
                    : '/new/sessions/new';
            }
            // fos-web's past-recording state shows the Past list; it doesn't use session_no.
            if (toState.name === 'storm.sessions.past-recording') {
                return '/new/sessions/past';
            }
            // Opening a template in sora schedules a session from it.
            if (toState.name === 'storm.sessions.template-detail') {
                return id ? '/new/sessions/new?template=' + id : '/new/sessions/templates';
            }
            // Guests use sora's own guest room, reached through its join page; a guest who
            // joined here stays in this player.
            if (toState.name === 'storm.sessions.player' && $cookies.get('isGuest')) {
                return null;
            }
            // The All tab's ?action=&sessionId= opens that session's details in sora, with its
            // annotations, recordings or chat panel; insights is the details page itself.
            if (toState.name === 'storm.sessions.all' && toStateParams.sessionId) {
                var action = toStateParams.action;
                var panel = action && action !== 'insights' ? '&panel=' + encodeURIComponent(action) : '';
                return '/new/sessions/' + encodeURIComponent(toStateParams.sessionId) + '?from=all' + panel;
            }

            // FOS-21392: sora's content library has Contents and Documents tabs, and one details
            // page per item (by id alone) with Recognition and Insights as ?tab= tabs. A new
            // item (no id) is started from the list there. Content fields are a drawer on the
            // list rather than a page of their own.
            if (toState.name === 'storm.admin-contents' || toState.name === 'storm.admin-content-fields') {
                return '/new/content/contents';
            }
            if (toState.name === 'storm.content-documents') {
                return '/new/content/documents';
            }
            if (CONTENT_DETAIL_TABS.hasOwnProperty(toState.name)) {
                if (!id) {
                    return '/new/content/contents';
                }
                var tab = CONTENT_DETAIL_TABS[toState.name];
                var query = tab ? '?tab=' + tab : (toStateParams.copy ? '?copy=true' : '');
                return '/new/content/details/' + id + query;
            }

            // No URL for these params: stay in fos-web rather than redirect to a broken path.
            var href = $state.href(toState.name, toStateParams);
            return href ? '/new' + href : null;
        }

        // Whether this state has a sora equivalent whose flag is on for the tenant.
        function isModernizedState(stateName) {
            if (!stateName) {
                return false;
            }
            if (MODERNIZED_JOBS_AIRFORMS_STATES.indexOf(stateName) !== -1) {
                return Feature.isActiveStrict(MODERNIZED_JOBS_AIRFORMS_FEATURE);
            }
            if (MODERNIZED_UI_STATES.indexOf(stateName) !== -1) {
                return Feature.isActiveStrict(MODERNIZED_UI_FEATURE);
            }
            return false;
        }

        function initialize() {
            $rootScope.VERSION = VERSION;
            $rootScope.back = back;

            var stateChangeStart = $rootScope.$on('$stateChangeStart', function (event, toState, toStateParams) {
                $rootScope.isBulkUploadInProgress = false;
                $rootScope.isCopilotApiProcessing = false;

                // Check for workspace admin routes on non-master domains
                if (
                    toState.name &&
                    toState.name.startsWith('storm.admin-workspace') &&
                    !$rootScope.isMasterWorkspace
                ) {
                    event.preventDefault();
                    $state.go('storm.home');
                    return;
                }

                // FOS-20691: redirect direct/bookmarked navigation into the Jobs/AiRForms
                // experience to sora when the modernized flag is on for this tenant.
                // FOS-21392: AiR Insights, AiR Sessions, AiR Tickets and AiR Content do the
                // same on the modernized UI flag.
                var modernizedPath =
                    isModernizedState(toState.name)
                        ? resolveModernizedExperiencePath(toState, toStateParams)
                        : null;
                if (modernizedPath) {
                    event.preventDefault();
                    window.location.replace(window.location.origin + modernizedPath);
                    return;
                }

                if (!$rootScope.isJobExecutionActive && !$rootScope.isJobBoardActive) {
                    $rootScope.toState = toState;
                    $rootScope.toStateParams = toStateParams;

                    if (Principal.isIdentityResolved()) {
                        Auth.authorize();
                    }
                } else {
                    event.preventDefault();
                    var iFrame = document.getElementById('ngxFrame').contentWindow;
                    var targetOrigin = postMessageSecurity.getNgxIframeTargetOrigin();
                    iFrame.postMessage({ 'jobExecutionCompleted': false, 'menuState': toState.name, 'type': 'leftNavChange' }, targetOrigin);
                }
            });

            var stateChangeSuccess = $rootScope.$on('$stateChangeSuccess', function (event, toState, toParams, fromState, fromParams) {
                // Remember previous state unless we've been redirected to login or we've just
                // reset the state memory after logout. If we're redirected to login, our
                // previousState is already set in the authExpiredInterceptor. If we're going
                // to login directly, we don't want to be sent to some previous state anyway
                if (!$rootScope.redirected && $rootScope.previousStateName) {
                    $rootScope.previousStateName = fromState.name;
                    $rootScope.previousStateParams = fromParams;
                }

                // Update the language
                JhiLanguageService.getCurrent().then(function (language) {
                    $translate.fallbackLanguage('en_US');
                    $translate.use(language);
                });

            });

            $rootScope.$on('$destroy', function () {
                if (angular.isDefined(stateChangeStart) && stateChangeStart !== null) {
                    stateChangeStart();
                }
                if (angular.isDefined(stateChangeSuccess) && stateChangeSuccess !== null) {
                    stateChangeSuccess();
                }
            });

            function back() {
                // If previous state is 'activate' or do not exist go to 'home'
                if ($rootScope.previousStateName === 'activate' || $state.get($rootScope.previousStateName) === null) {
                    $state.go('home');
                } else {
                    $state.go($rootScope.previousStateName, $rootScope.previousStateParams);
                }
            }
        }
    }
})();
